// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import LazyImage, { ImageSettingKey, LazyImageProps } from "@rpsc/MobiusOverrides/LazyImage";
import Link, { LinkPresentationProps } from "@rpsc/MobiusOverrides/Link/Link";
import { mergeWithCss } from "@rpsc/XDL/Utilities";
import React, { FC, useState } from "react";

interface CategoryCardProps {
    category?: Category;
    showImages?: boolean;
    extendedStyles?: CategoryCardStyles;
}

type Props = CategoryCardProps;

export interface CategoryCardStyles {
    cardContainer?: InjectableCss;
    categoryImageLink?: ClickablePresentationProps;
    categoryImage?: LazyImageProps;
    categoryNameLink?: LinkPresentationProps;
}

export const categoryCardStyles: CategoryCardStyles = {};

const CategoryCard: FC<Props> = ({ category, showImages = true, extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(categoryCardStyles, extendedStyles));

    if (!category) {
        return null;
    }
    const path = category.path || (category as any).url; // Handle featured Category
    return (
        <StyledWrapper {...styles.cardContainer}>
            {showImages && (
                <Clickable href={path} {...styles.categoryImageLink}>
                    <LazyImage
                        src={category.smallImagePath}
                        altText={category.imageAltText}
                        imgProps={{ "data-test-selector": "categoryCard_image" } as any}
                        {...styles.categoryImage}
                        {...{ imageKey: ImageSettingKey.category }}
                    />
                </Clickable>
            )}

            <Link href={path} {...styles.categoryNameLink} aria-label={category.imageAltText}>
                {category.shortDescription}
            </Link>
        </StyledWrapper>
    );
};

export default CategoryCard;
